// src/pages/Calendar.js
import React, { useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';

import useEvents from '../hooks/useEvents';
import useVehicles from '../hooks/useVehicles';

import CalendarView from '../components/Calendar/CalendarView';
import CalendarSidebar from '../components/Calendar/CalendarSidebar';
import EditDatesDialog from '../components/Calendar/EditDatesDialog';

// Normalize VRM (remove spaces, uppercase)
function normalizeVrm(vrm) {
  return (vrm || '').toUpperCase().replace(/\s+/g, '');
}

// Convert UNIX seconds → input value for datetime-local
function toLocalInputValueFromUnix(unixSeconds) {
  if (!unixSeconds) return '';
  const d = new Date(unixSeconds * 1000);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert datetime-local → ISO string (for backend)
function fromInputToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function CalendarPage() {
  const { events, refreshEvents } = useEvents();
  const { vehicles, refreshVehicles, updateVehicleDates } = useVehicles();

  const [vrmFilter, setVrmFilter] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editVrm, setEditVrm] = useState('');
  const [editValues, setEditValues] = useState({
    plateExpiry: '',
    interimTest: '',
    bookingTest: '',
  });

  const calendarRef = useRef(null);

  // Filter events by VRM
  const filteredEvents = useMemo(() => {
    if (!vrmFilter.trim()) return events;
    const q = vrmFilter.trim().toLowerCase();
    return events.filter(
      (e) => (e.extendedProps?.vrm || '').toLowerCase().includes(q)
    );
  }, [events, vrmFilter]);

  // Filter vehicles by VRM
  const filteredVehicles = useMemo(() => {
    if (!vrmFilter.trim()) return vehicles;
    const q = vrmFilter.trim().toLowerCase();
    return vehicles.filter((v) => (v.vrm || '').toLowerCase().includes(q));
  }, [vehicles, vrmFilter]);

  // Style events by type
  const eventClassNames = (arg) => {
    const type = arg.event.extendedProps.type;
    if (type === 'plate_expiry') return ['event-plate-expiry'];
    if (type === 'interim_test') return ['event-interim-test'];
    if (type === 'booking_test') return ['event-booking-test'];
    return [];
  };

  // Get earliest event for VRM
  function getFirstEventForVrm(vrm) {
    const norm = normalizeVrm(vrm);
    const matches = events.filter(
      (e) => normalizeVrm(e.extendedProps?.vrm) === norm
    );
    if (!matches.length) return null;
    matches.sort((a, b) => new Date(a.start) - new Date(b.start));
    return matches[0];
  }

  // When VRM is clicked in sidebar → jump calendar to date
  const handleVrmClick = (vrm) => {
    setVrmFilter(vrm);
    const first = getFirstEventForVrm(vrm);
    if (!first) return;

    const api = calendarRef.current?.getApi();
    if (api && first.start) {
      api.gotoDate(new Date(first.start));
    }
  };

  // Open edit modal populated with current values
  const handleEditClick = (vrm) => {
    setEditVrm(vrm);

    const vehicle = vehicles.find(
      (v) => normalizeVrm(v.vrm) === normalizeVrm(vrm)
    );
    const phv = vehicle?.phv || {};

    setEditValues({
      plateExpiry: toLocalInputValueFromUnix(phv.plate_expiry_timestamp),
      interimTest: toLocalInputValueFromUnix(phv.interim_test_timestamp),
      bookingTest: toLocalInputValueFromUnix(phv.booking_test_timestamp),
    });

    setEditOpen(true);
  };

  // Clicking an event also opens the edit modal
  const handleEventClick = (event) => {
    const vrm = event.extendedProps?.vrm;
    if (vrm) handleEditClick(vrm);
  };

  // Clicking a date in month view → drill down to day view
  const handleDateClick = (date) => {
    const api = calendarRef.current?.getApi();
    if (!api) return;

    if (api.view.type === 'dayGridMonth') {
      api.changeView('timeGridDay', date);
    }
  };

  const handleEditFieldChange = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  // Save changes to backend
  const handleSaveEdit = async () => {
    const body = {
      vrm: editVrm,
      plate_expiry: fromInputToIso(editValues.plateExpiry),
      interim_test: fromInputToIso(editValues.interimTest),
      booking_test: fromInputToIso(editValues.bookingTest),
    };

    await updateVehicleDates(editVrm, body);
    await refreshEvents();
    await refreshVehicles();
    setEditOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, height: 'calc(100vh - 96px)' }}>
      <CalendarSidebar
        vehicles={filteredVehicles}
        vrmFilter={vrmFilter}
        onVrmFilterChange={setVrmFilter}
        onVrmClick={handleVrmClick}
        onEditClick={handleEditClick}
      />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          borderRadius: 2,
          boxShadow: '0 18px 40px rgba(0,0,0,0.12)',
          backgroundColor: 'white',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CalendarView
          events={filteredEvents}
          calendarRef={calendarRef}
          onEventClick={handleEventClick}
          onDateClick={handleDateClick}
          eventClassNames={eventClassNames}
        />
      </Box>

      <EditDatesDialog
        open={editOpen}
        vrm={editVrm}
        values={editValues}
        onChangeField={handleEditFieldChange}
        onClose={() => setEditOpen(false)}
        onSave={handleSaveEdit}
      />
    </Box>
  );
}
